/** Общие константы UI: статусы, этапы, роли, действия. */

import type {
  ApprovalStepStatus,
  ApprovalStepType,
  Process,
  ProcessStatus,
  RequestType,
} from "../types";

export const STATUS_LABEL: Record<ProcessStatus, string> = {
  in_progress: "В работе",
  waiting_supplier_reply: "Ждём поставщика",
  waiting_offers: "Ждём КП",
  waiting_user_review: "Согласование",
  waiting_sbis_review: "Ждём СБИС",
  waiting_human: "Нужно исправление",
  completed: "Завершён",
  failed: "Ошибка",
  cancelled: "Отменён",
};

/**
 * Подпись статуса для заявок руководителя (сценарий C).
 * Отклонение согласующим — это штатный исход («Отклонена»), а не «Ошибка».
 */
export function requestStatusLabel(process: {
  process_type: string;
  status: ProcessStatus;
  route_state?: { final_decision?: string | null } | null;
}): string {
  if (
    REQUEST_PROCESS_TYPES.includes(process.process_type) &&
    process.status === "failed" &&
    process.route_state?.final_decision === "rejected"
  ) {
    return "Отклонена";
  }
  return STATUS_LABEL[process.status] ?? process.status;
}

export const STATUS_HINT: Record<ProcessStatus, string> = {
  in_progress: "Агенты работают над заявкой автоматически",
  waiting_supplier_reply: "Письмо поставщику отправлено, ждём счёт",
  waiting_offers: "Идёт сбор коммерческих предложений от поставщиков",
  waiting_user_review: "Согласующий (руководитель) должен рассмотреть заявку",
  waiting_sbis_review: "Документы ожидают проверки в СБИС",
  waiting_human: "Закупщику нужно устранить причину остановки агента",
  completed: "Процесс завершён, оплата проведена",
  failed: "Ошибка в одном из шагов; требуется вмешательство",
  cancelled: "Заявка отменена",
};

const REQUEST_PROCESS_TYPES: readonly string[] = [
  "purchase_request",
  "tech_task_contract",
  "stock_check",
  "service_request",
];

/** Это внутренняя заявка руководителя (сценарий C), а не процесс закупки. */
export function isRequestProcessType(processType: string): boolean {
  return REQUEST_PROCESS_TYPES.includes(processType);
}

/** Путь детальной страницы: заявки живут в /requests, закупки — в /processes. */
export function processDetailPath(p: { process_id: string; process_type: string }): string {
  return isRequestProcessType(p.process_type)
    ? `/requests/${p.process_id}`
    : `/processes/${p.process_id}`;
}

/**
 * Подсказка статуса для карточки процесса.
 * Для внутренних заявок подсказка строится по current_stage (не по status),
 * для procurement-процессов — обычный STATUS_HINT.
 */
export function requestStatusHint(process: {
  process_type: string;
  current_stage: string;
  status: ProcessStatus;
  context?: { conversion?: { result?: string } | null } | null;
  route_state?: { current_position: number; steps: { name: string }[] } | null;
}): string {
  if (!REQUEST_PROCESS_TYPES.includes(process.process_type)) {
    return STATUS_HINT[process.status] ?? process.status;
  }
  switch (process.current_stage) {
    case "waiting_approval": {
      const rs = process.route_state;
      const cur = rs ? rs.steps[rs.current_position] : undefined;
      return cur ? `на согласовании: ${cur.name}` : "на согласовании";
    }
    case "waiting_initiator":
      return "возвращена на доработку инициатору";
    case "route_resolution":
    case "intake":
      return "черновик";
    case "conversion":
      return "передаётся в закупку";
    case "completed":
      return process.context?.conversion?.result === "awaiting_manual_route_choice"
        ? "согласована · ждёт передачи в закупку (закупщик выберет маршрут)"
        : "согласована";
    case "failed":
      return "отклонена";
    default:
      return STATUS_HINT[process.status] ?? process.status;
  }
}

/** Русские названия типов событий аудит-лога (для вкладки «История»). */
export const EVENT_TYPE_LABEL: Record<string, string> = {
  "sbis.document_received": "Документ из СБИС",
  "request.created": "Заявка создана",
  "email.received": "Входящее письмо",
  "agent.letter_sent": "Письмо отправлено",
  "agent.reconciliation_completed": "Сверка выполнена",
  "agent.supplier_search_completed": "Поиск поставщиков завершён",
  "agent.tender_request_sent": "Приглашения на КП разосланы",
  "agent.recommendation_ready": "Рекомендация ИИ готова",
  "agent.registry_update_requested": "Запрос записи в реестр",
  "agent.intent_determined": "ИИ определил намерение",
  "supplier.invoice_received": "Счёт от поставщика",
  "supplier.offer_received": "КП от поставщика",
  "supplier.registered": "Поставщик зарегистрирован",
  "supplier.moderated": "Модерация поставщика",
  "item.published": "Позиция опубликована",
  "item.offer_submitted": "КП по позиции",
  "item.offer_updated": "КП изменено поставщиком",
  "item.offer_withdrawn": "КП отозвано поставщиком",
  "item.award_proposed": "Предложен победитель",
  "item.awarded": "Победитель утверждён",
  "item.award_rejected": "Победитель отклонён",
  "item.in_delivery": "Позиция отгружена",
  "item.delivered": "Позиция поставлена",
  "item.needs_manual": "Позиции требуют вмешательства",
  "user.approval_decision": "Решение согласующего",
  "user.reply": "Сообщение",
  "stage.entered": "Этап начат",
  "stage.left": "Этап завершён",
  "process.started": "Процесс запущен",
  "process.completed": "Процесс завершён",
  "process.failed": "Процесс остановлен с ошибкой",
  "process.escalated": "Эскалация по SLA",
  "system.registry_written": "Записано в реестр",
  "error.sbis_unavailable": "СБИС недоступен",
  "error.model": "Ошибка модели ИИ",
  "error.agent": "Ошибка ИИ-агента",
  "error.registry": "Ошибка реестра",
  "error.unhandled": "Необработанная ошибка",
};

/** Кто совершил действие (actor_type в событиях). */
export const ACTOR_TYPE_LABEL: Record<string, string> = {
  user: "пользователь",
  agent: "ИИ-агент",
  system: "система",
  supplier: "поставщик",
};

export const STAGE_LABEL: Record<string, string> = {  intake: "Приём заявки",
  category: "Категория",
  // ---- Сценарий A (fixed_supplier) ----
  supplier_lookup: "Поставщик",
  letter: "Письмо поставщику",
  send: "Отправка письма",
  waiting_supplier_reply: "Ответ поставщика",
  invoice: "Счёт",
  reconciliation: "Сверка",
  approval: "Согласование",
  registry_update: "Реестр",
  execution: "Поставка",
  payment: "Оплата",
  closed: "Завершено",
  failed: "Ошибка",
  cancelled: "Отменён",
  // ---- Сценарий B (sourcing_tender) ----
  supplier_search: "Поиск поставщиков",
  tender_request: "Запрос КП",
  waiting_offers: "Сбор КП",
  offer_comparison: "Сравнение КП",
  waiting_human: "Ожидание человека",
  // ---- Сценарий C (заявки руководителя) ----
  route_resolution: "Определение маршрута",
  waiting_approval: "Согласование по маршруту",
  conversion: "Передача в закупку",
  stock_check_pending: "Проверка наличия на складе",
  contract_pending: "Оформление договора",
  waiting_initiator: "Ожидание доработки инициатором",
  // ---- Ручной режим (manual_procurement) ----
  supplier_selection: "Поставщик",
  waiting_supplier: "Оферта",
  ordering: "Заказ",
  delivery: "Поставка",
  // ---- Свободный поставщик (free_supplier) ----
  portal_publish: "Публикация на портале",
  award_review: "Выбор победителей",
};

// ===== Стадии процесса: группы для фильтра и цвета бейджей =====

export interface StageFilterGroup {
  label: string;
  stages: string[];
}

/** Группы стадий для выпадающего фильтра в списке процессов. */
export const STAGE_FILTER_GROUPS: StageFilterGroup[] = [
  {
    label: "Старт",
    stages: ["intake", "category"],
  },
  {
    label: "Поставщик и счёт",
    stages: [
      "supplier_lookup",
      "supplier_selection",
      "letter",
      "send",
      "waiting_supplier_reply",
      "waiting_supplier",
      "invoice",
      "reconciliation",
    ],
  },
  {
    label: "Тендер",
    stages: ["supplier_search", "tender_request", "waiting_offers", "offer_comparison"],
  },
  {
    label: "Портал поставщиков",
    stages: ["portal_publish", "award_review"],
  },
  {
    label: "Согласование и завершение",
    stages: ["approval", "registry_update", "ordering", "execution", "delivery", "payment", "closed"],
  },
  {
    label: "Служебные",
    stages: ["waiting_human", "failed", "cancelled"],
  },
];

/** Tailwind-классы бейджа по стадии процесса. */
export const STAGE_BADGE: Record<string, string> = {
  intake: "badge-gray",
  category: "badge-gray",
  supplier_lookup: "badge-blue",
  supplier_selection: "badge-blue",
  letter: "badge-blue",
  send: "badge-blue",
  waiting_supplier_reply: "badge-sky",
  waiting_supplier: "badge-sky",
  invoice: "badge-amber",
  reconciliation: "badge-amber",
  supplier_search: "badge-blue",
  tender_request: "badge-blue",
  waiting_offers: "badge-sky",
  offer_comparison: "badge-purple",
  approval: "badge-purple",
  registry_update: "badge-teal",
  ordering: "badge-teal",
  execution: "badge-teal",
  delivery: "badge-teal",
  payment: "badge-green",
  closed: "badge-green",
  waiting_human: "badge-orange",
  portal_publish: "badge-blue",
  award_review: "badge-purple",
  failed: "badge-red",
  cancelled: "badge-gray",
};

/** Бейдж стадии с подписью; unknown-коды показываем как есть. */
export function stageBadgeClass(stage: string): string {
  return STAGE_BADGE[stage] || "badge-gray";
}

// ===== Шаги маршрутов закупки (для степпера на странице процесса) =====

export interface FlowStep {
  stage: string;
  label: string;
  icon: string;
  /** Все стадии движка, которые относятся к этому шагу (из снимка шаблона). */
  stages?: string[];
}

/** Ручной режим (manual_procurement). */
export const MANUAL_FLOW_STEPS: FlowStep[] = [
  { stage: "supplier_selection", label: "Поставщик", icon: "🏭" },
  { stage: "waiting_supplier", label: "Оферта", icon: "📨" },
  { stage: "invoice", label: "Счёт", icon: "💳" },
  { stage: "reconciliation", label: "Сверка", icon: "🔍" },
  { stage: "approval", label: "Согласование", icon: "✅" },
  { stage: "ordering", label: "Заказ", icon: "🛒" },
  { stage: "delivery", label: "Поставка", icon: "📦" },
  { stage: "payment", label: "Оплата", icon: "💰" },
];

/** Фиксированный поставщик (fixed_supplier, AI-агенты). */
export const FIXED_SUPPLIER_FLOW_STEPS: FlowStep[] = [
  { stage: "supplier_lookup", label: "Поставщик", icon: "🏭" },
  { stage: "letter", label: "Письмо", icon: "✉️" },
  { stage: "waiting_supplier_reply", label: "Ответ", icon: "📨" },
  { stage: "invoice", label: "Счёт", icon: "💳" },
  { stage: "reconciliation", label: "Сверка", icon: "🔍" },
  { stage: "approval", label: "Согласование", icon: "✅" },
  { stage: "registry_update", label: "Реестр", icon: "📒" },
  { stage: "execution", label: "Поставка", icon: "📦" },
  { stage: "payment", label: "Оплата", icon: "💰" },
];

/** Тендер с поиском (sourcing_tender, AI-агенты). */
export const TENDER_FLOW_STEPS: FlowStep[] = [
  { stage: "supplier_search", label: "Поиск", icon: "🔎" },
  { stage: "tender_request", label: "Запрос КП", icon: "📤" },
  { stage: "waiting_offers", label: "Сбор КП", icon: "📥" },
  { stage: "offer_comparison", label: "Сравнение", icon: "⚖️" },
  { stage: "approval", label: "Согласование", icon: "✅" },
  { stage: "registry_update", label: "Реестр", icon: "📒" },
  { stage: "execution", label: "Поставка", icon: "📦" },
  { stage: "payment", label: "Оплата", icon: "💰" },
];

/** Свободный поставщик (free_supplier): позиции публикуются на портале поставщиков. */
export const FREE_SUPPLIER_FLOW_STEPS: FlowStep[] = [
  { stage: "portal_publish", label: "Публикация на портале", icon: "📣" },
  { stage: "waiting_offers", label: "Сбор КП", icon: "📥" },
  { stage: "award_review", label: "Выбор победителей", icon: "⚖️" },
  { stage: "registry_update", label: "Запись в реестр", icon: "📒" },
  { stage: "execution", label: "Исполнение", icon: "📦" },
  { stage: "payment", label: "Оплата", icon: "💰" },
];

export const FLOW_STEPS_BY_TYPE: Record<string, FlowStep[]> = {
  manual_procurement: MANUAL_FLOW_STEPS,
  fixed_supplier: FIXED_SUPPLIER_FLOW_STEPS,
  sourcing_tender: TENDER_FLOW_STEPS,
  free_supplier: FREE_SUPPLIER_FLOW_STEPS,
};

// ===== Варианты маршрутов при предоплате (payment_flow v1, terms=prepay) =====
// Отличаются только порядком: оплата идёт ДО поставки/исполнения.
// Применяются только когда снапшот context.payment_flow подтверждает prepay
// (см. lib/paymentFlow.ts). Резервные типы (free_supplier и др.) — legacy,
// для них prepay-варианта нет.

/** Ручной режим, предоплата: … → Заказ → Оплата → Поставка. */
export const MANUAL_FLOW_STEPS_PREPAY: FlowStep[] = [
  { stage: "supplier_selection", label: "Поставщик", icon: "🏭" },
  { stage: "waiting_supplier", label: "Оферта", icon: "📨" },
  { stage: "invoice", label: "Счёт", icon: "💳" },
  { stage: "reconciliation", label: "Сверка", icon: "🔍" },
  { stage: "approval", label: "Согласование", icon: "✅" },
  { stage: "ordering", label: "Заказ", icon: "🛒" },
  { stage: "payment", label: "Оплата", icon: "💰" },
  { stage: "delivery", label: "Поставка", icon: "📦" },
];

/** Фиксированный поставщик, предоплата: … → Реестр → Оплата → Поставка. */
export const FIXED_SUPPLIER_FLOW_STEPS_PREPAY: FlowStep[] = [
  { stage: "supplier_lookup", label: "Поставщик", icon: "🏭" },
  { stage: "letter", label: "Письмо", icon: "✉️" },
  { stage: "waiting_supplier_reply", label: "Ответ", icon: "📨" },
  { stage: "invoice", label: "Счёт", icon: "💳" },
  { stage: "reconciliation", label: "Сверка", icon: "🔍" },
  { stage: "approval", label: "Согласование", icon: "✅" },
  { stage: "registry_update", label: "Реестр", icon: "📒" },
  { stage: "payment", label: "Оплата", icon: "💰" },
  { stage: "execution", label: "Поставка", icon: "📦" },
];

/** Тендер с поиском, предоплата: … → Реестр → Оплата → Поставка. */
export const TENDER_FLOW_STEPS_PREPAY: FlowStep[] = [
  { stage: "supplier_search", label: "Поиск", icon: "🔎" },
  { stage: "tender_request", label: "Запрос КП", icon: "📤" },
  { stage: "waiting_offers", label: "Сбор КП", icon: "📥" },
  { stage: "offer_comparison", label: "Сравнение", icon: "⚖️" },
  { stage: "approval", label: "Согласование", icon: "✅" },
  { stage: "registry_update", label: "Реестр", icon: "📒" },
  { stage: "payment", label: "Оплата", icon: "💰" },
  { stage: "execution", label: "Поставка", icon: "📦" },
];

/** Prepay-варианты маршрутов (только типы с payment_flow v1). */
export const FLOW_STEPS_BY_TYPE_PREPAY: Record<string, FlowStep[]> = {
  manual_procurement: MANUAL_FLOW_STEPS_PREPAY,
  fixed_supplier: FIXED_SUPPLIER_FLOW_STEPS_PREPAY,
  sourcing_tender: TENDER_FLOW_STEPS_PREPAY,
};

/** Маппинг «системной» стадии на индекс шага в степпере маршрута. */
const STAGE_TO_FLOW_INDEX: Record<string, Record<string, number>> = {
  manual_procurement: {
    intake: 0,
    supplier_selection: 0,
    waiting_supplier: 1,
    invoice: 2,
    reconciliation: 3,
    approval: 4,
    ordering: 5,
    delivery: 6,
    payment: 7,
  },
  fixed_supplier: {
    intake: 0,
    category: 0,
    supplier_lookup: 0,
    letter: 1,
    send: 1,
    waiting_supplier_reply: 2,
    invoice: 3,
    reconciliation: 4,
    approval: 5,
    send_approval_letter: 5,
    send_rejection_letter: 5,
    registry_update: 6,
    execution: 7,
    payment: 8,
  },
  sourcing_tender: {
    intake: 0,
    category: 0,
    supplier_search: 0,
    tender_request: 1,
    waiting_offers: 2,
    offer_comparison: 3,
    approval: 4,
    registry_update: 5,
    execution: 6,
    payment: 7,
  },
  free_supplier: {
    intake: 0,
    category: 0,
    portal_publish: 0,
    waiting_offers: 1,
    award_review: 2,
    approval: 2,
    registry_update: 3,
    execution: 4,
    payment: 5,
  },
};

/** Маппинг стадий на индексы шагов при предоплате (payment_flow v1 prepay):
 *  оплата идёт раньше поставки/исполнения. */
const STAGE_TO_FLOW_INDEX_PREPAY: Record<string, Record<string, number>> = {
  manual_procurement: {
    intake: 0,
    supplier_selection: 0,
    waiting_supplier: 1,
    invoice: 2,
    reconciliation: 3,
    approval: 4,
    ordering: 5,
    payment: 6,
    delivery: 7,
  },
  fixed_supplier: {
    intake: 0,
    category: 0,
    supplier_lookup: 0,
    letter: 1,
    send: 1,
    waiting_supplier_reply: 2,
    invoice: 3,
    reconciliation: 4,
    approval: 5,
    send_approval_letter: 5,
    send_rejection_letter: 5,
    registry_update: 6,
    payment: 7,
    execution: 8,
  },
  sourcing_tender: {
    intake: 0,
    category: 0,
    supplier_search: 0,
    tender_request: 1,
    waiting_offers: 2,
    offer_comparison: 3,
    approval: 4,
    registry_update: 5,
    payment: 6,
    execution: 7,
  },
};

/** Индекс текущего шага процесса в степпере его маршрута.
 *  paymentFirst=true — маршрут с предоплатой (оплата до поставки),
 *  подтверждённой снапшотом payment_flow v1 (см. lib/paymentFlow.ts). */
export function flowStepIndex(
  processType: string,
  stage: string,
  paymentFirst = false,
): number {
  const map = paymentFirst
    ? (STAGE_TO_FLOW_INDEX_PREPAY[processType] ?? STAGE_TO_FLOW_INDEX[processType])
    : STAGE_TO_FLOW_INDEX[processType];
  // Unknown stages must not masquerade as the first step in the visual overview.
  if (!map) return -1;
  return map[stage] ?? -1;
}

/** Короткий суффикс маршрута для стадий с одинаковыми названиями. */
export const STAGE_ROUTE_SHORT: Record<string, string> = {
  supplier_lookup: "фикс.",
  letter: "фикс.",
  send: "фикс.",
  waiting_supplier_reply: "фикс.",
  supplier_selection: "ручной",
  waiting_supplier: "ручной",
  ordering: "ручной",
  delivery: "ручной",
  supplier_search: "тендер",
  tender_request: "тендер",
  waiting_offers: "тендер",
  offer_comparison: "тендер",
  portal_publish: "портал",
  award_review: "портал",
  execution: "ИИ",
};

// ----------------- routing types (выбор при создании процесса) -----------------
export interface RouteTypeInfo {
  label: string;
  hint: string;
  description: string;
}

export const ROUTE_TYPE_INFO: Record<string, RouteTypeInfo> = {
  manual_procurement: {
    label: "Ручной режим",
    hint: "Поставщик → счёт → договор → оплата — всё вручную",
    description:
      "Без AI-агентов. Закупщик сам заполняет поставщика, счёт, договор, прикладывает документы и завершает закупку. Все данные попадают в базу для будущего AI-анализа цен.",
  },
  fixed_supplier: {
    label: "Закреплённый поставщик",
    hint: "Письмо-заявка → счёт → сверка → согласование",
    description:
      "Для закупок у единственного поставщика. Категория уже привязана к нему в «Категории». AI отправляет письмо-заявку, ждёт счёт, сверяет с позициями.",
  },
  sourcing_tender: {
    label: "Тендер с поиском",
    hint: "AI ищет → рассылает КП → сводка → победитель",
    description:
      "AI-агент ищет поставщиков в интернете, рассылает приглашения на КП, через дедлайн показывает сравнительную таблицу. Вы выбираете победителя → согласование.",
  },
  free_supplier: {
    label: "Свободный поставщик",
    hint: "Публикация на портале → сбор КП → победители по позициям",
    description:
      "После согласования заявки её позиции публикуются на портале поставщиков для сбора КП. Закупщик выбирает победителей по позициям, после поставки всех позиций заявка закрывается.",
  },
  marketplace_search: {
    label: "Маркетплейс (Ozon/WB и т.п.)",
    hint: "Заказ → чек/накладная → оплата",
    description:
      "Закупка на Ozon, Wildberries, Яндекс.Маркет. Подтверждение заказа приходит чеком или накладной маркетплейса.",
  },
  framework_contract: {
    label: "Рамочный договор",
    hint: "Спецификация → подтверждение → оплата",
    description:
      "Условия согласованы заранее в рамочном договоре. Заявка = конкретная спецификация к нему.",
  },
  recurring_supply: {
    label: "Регулярная поставка",
    hint: "Таймер / остатки → автоматический заказ",
    description:
      "Запускается по таймеру или при достижении минимального остатка. Не от СБИС — отдельный триггер.",
  },
  service_procurement: {
    label: "Услуги (работы)",
    hint: "Заявка → договор → акт → оплата",
    description:
      "Другой жизненный цикл: вместо «получено на складе» — «работы приняты по акту».",
  },
  sole_source: {
    label: "Единственный источник",
    hint: "Только с обоснованием → строгое согласование",
    description:
      "Закупка у единственного возможного поставщика. Перед согласованием обязательно блок с обоснованием выбора (нет альтернатив).",
  },
  // ---- Типы заявок руководителя ----
  purchase_request: {
    label: "Заявка на приобретение",
    hint: "Согласование → снабжение → закупка",
    description:
      "Согласование с руководителями → автоматически создаётся процесс закупки (fixed_supplier или sourcing_tender по категории).",
  },
  tech_task_contract: {
    label: "Тех. задание + договор поставки",
    hint: "Согласование → юристы → договор → закупка",
    description:
      "После согласования — оформление договора через юридическую службу. После подписания договора создаётся процесс закупки.",
  },
  stock_check: {
    label: "Проверка наличия на складе",
    hint: "Согласование → проверка склада → закупка (если нет)",
    description:
      "Сначала проверяется наличие на складе. Если найдено — закрывается. Если нет — автоматически создаётся заявка на приобретение.",
  },
  service_request: {
    label: "Заявка на услуги/работы",
    hint: "Согласование → договор услуг → выполнение",
    description:
      "Закупка работ/услуг (ремонт, консалтинг, обучение). После согласования — отдельный процесс выполнения.",
  },
};

export const ROUTE_TYPE_ORDER: string[] = [
  "manual_procurement",
  "fixed_supplier",
  "sourcing_tender",
  "free_supplier",
];

/**
 * Активные маршруты закупки:
 * ручной, фиксированный поставщик, тендер, свободный поставщик.
 * Использовать во всех селектах выбора маршрута.
 */
export const ACTIVE_PROCUREMENT_ROUTES: string[] = [...ROUTE_TYPE_ORDER];

export const REQUEST_TYPE_ORDER: RequestType[] = [
  "purchase_request",
  "tech_task_contract",
  "stock_check",
  "service_request",
];

export const ROUTE_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(ROUTE_TYPE_INFO).map(([k, v]) => [k, v.label]),
);

export type Role =
  | "purchaser"
  | "approver"
  | "operator"
  | "analyst"
  | "admin"
  /** Кладовщик: приёмка на складе с планшета. */
  | "warehouse"
  | "supplier";

export const ROLE_LABEL: Record<Role, string> = {
  purchaser: "Закупщик",
  approver: "Согласующий",
  operator: "Оператор",
  analyst: "Аналитик",
  admin: "Администратор",
  warehouse: "Кладовщик",
  supplier: "Поставщик",
};

export const ROLE_DESCRIPTION: Record<Role, string> = {
  purchaser:
    "Закупщик (служба снабжения): создаёт процессы закупки, отвечает поставщикам, контролирует поставки и оплату. Имеет доступ ко всем процессам в работе.",
  approver:
    "Согласующий (руководитель / юрист / ГД): рассматривает заявки операторов, визирует решения. Может быть назначен на конкретные шаги маршрута.",
  operator:
    "Оператор подразделения: инициатор заявок от своего отдела. Создаёт заявки на приобретение / услуги / договоры / проверку склада, видит только свои заявки и проходит пошаговое согласование по маршруту. ИИ-агенты включаются только после согласования и передачи в службу снабжения.",
  analyst:
    "Аналитик / Аудитор: read-only по всем процессам, доступ к аудит-логу и выгрузке истории. Не участвует в операционных действиях.",
  admin:
    "Администратор: полный доступ. Управляет пользователями, отделами, маршрутами согласования, маршрутами закупок, привязками отделов к маршрутам. Видит все процессы и заявки.",
  warehouse:
    "Кладовщик: приёмка на складе с планшета. Видит только ожидаемые поставки своих юридических лиц — без цен и сумм — и оформляет приёмку с фото накладной. В закупки, поставщиков и отчёты доступа нет.",
  supplier:
    "Поставщик (внешний): ограниченный Portal для ответа на КП и подтверждения заказов. Доступ через отдельный интерфейс (в разработке).",
};

export const ROLE_HINT: Record<Role, string> = {
  purchaser: "создаёт заявки, исправляет ошибки агентов",
  approver: "рассматривает и согласовывает заявки",
  operator: "создаёт заявки от своего подразделения, видит только свои",
  analyst: "только чтение: аудит-лог и отчёты",
  admin: "управление пользователями, маршрутами, всеми процессами",
  warehouse: "приёмка на складе с планшета",
  supplier: "ограниченный Portal (внешний)",
};

// ===== Реальные категории закупок (ProductCategory) =====
// Это НЕ маршруты ИИ — это «что закупаем» (ноутбуки, бумага, чиллеры).
export const PRODUCT_CATEGORY_LABEL: Record<string, string> = {
  it_equipment: "IT-оборудование",
  office_supplies: "Канцелярия и расходники",
  household: "Хозтовары и бытовая химия",
  spare_parts: "Запчасти и комплектующие",
  chiller_parts: "Чиллеры и комплектующие",
  food: "Продукты питания",
  medical_supplies: "Медицинские расходники",
  services: "Услуги и работы",
  construction: "Строительные материалы",
  tools: "Инструменты",
  software: "ПО и лицензии",
  furniture: "Мебель",
};

export const PRODUCT_CATEGORY_ICON: Record<string, string> = {
  it_equipment: "💻",
  office_supplies: "📎",
  household: "🧴",
  spare_parts: "🔧",
  chiller_parts: "❄",
  food: "🍞",
  medical_supplies: "💊",
  services: "🛠",
  construction: "🧱",
  tools: "🔧",
  software: "💿",
  furniture: "🪑",
};

export const PRODUCT_CATEGORY_DESCRIPTION: Record<string, string> = {
  it_equipment: "Ноутбуки, компьютеры, серверы, сетевое оборудование, принтеры, МФУ.",
  office_supplies: "Бумага, ручки, карандаши, папки, файлы, скрепки, маркеры, скотч.",
  household: "Мыло, бумажные полотенца, туалетная бумага, чистящие средства.",
  spare_parts: "Запасные части для оборудования, комплектующие, расходные материалы для ремонта.",
  chiller_parts: "Холодильное оборудование, чиллеры, компрессоры, теплообменники.",
  food: "Продукты для столовой: молоко, хлеб, мясо, овощи, фрукты, напитки.",
  medical_supplies: "Медикаменты, расходные медицинские материалы, средства защиты.",
  services: "Сторонние услуги: ремонт, обслуживание, консалтинг, обучение, уборка.",
  construction: "Цемент, кирпич, сухие смеси, утеплители, крепёж, отделочные материалы.",
  tools: "Перфораторы, дрели, шуруповёрты, электро- и ручной инструмент.",
  software: "ПО, лицензии, подписки на SaaS-сервисы, облачные ресурсы.",
  furniture: "Офисная мебель: столы, стулья, шкафы, тумбы.",
};

// ===== Понятные названия ИИ-маршрутов (routing_type) =====
// Код (английский) → русское название + что делает.
export const ROUTE_TYPE_DISPLAY: Record<string, { label: string; description: string; icon: string }> = {
  fixed_supplier: {
    label: "Фиксированный поставщик",
    description:
      "AI отправляет письмо-заявку закреплённому поставщику, ждёт счёт, сверяет позиции и отправляет на согласование.",
    icon: "📨",
  },
  sourcing_tender: {
    label: "Тендер с поиском поставщиков",
    description:
      "AI ищет поставщиков в интернете, рассылает приглашения на КП, через дедлайн показывает сравнительную таблицу.",
    icon: "🔎",
  },
  free_supplier: {
    label: "Свободный поставщик",
    description:
      "Позиции заявки публикуются на портале поставщиков для сбора КП. Закупщик выбирает победителей по позициям.",
    icon: "📣",
  },
  marketplace_search: {
    label: "Маркетплейс (Ozon / WB)",
    description:
      "Заказ на маркетплейсе. Подтверждение приходит чеком или накладной.",
    icon: "🛒",
  },
  framework_contract: {
    label: "Рамочный договор",
    description:
      "Заявка = спецификация к действующему рамочному договору с согласованными условиями.",
    icon: "📄",
  },
  recurring_supply: {
    label: "Регулярная поставка",
    description:
      "Автоматический заказ по таймеру или при достижении минимального остатка на складе.",
    icon: "🔄",
  },
  service_procurement: {
    label: "Услуги (работы)",
    description:
      "Договор оказания услуг → акт → оплата (вместо приёмки товара).",
    icon: "🛠",
  },
  sole_source: {
    label: "Единственный источник",
    description:
      "Закупка у единственного возможного поставщика. Требуется обоснование выбора.",
    icon: "🎯",
  },
  manual_procurement: {
    label: "Ручная закупка (без ИИ)",
    description:
      "Без AI-агентов. Закупщик сам выбирает поставщика, ведёт согласование и оплату. Для нестандартных или срочных закупок.",
    icon: "👤",
  },
  // Сценарии оператора
  purchase_request: {
    label: "Заявка на приобретение (от оператора)",
    description:
      "Заявка руководителя подразделения. После согласования автоматически создаётся процесс закупки.",
    icon: "📝",
  },
  tech_task_contract: {
    label: "Тех. задание + договор (от оператора)",
    description:
      "После согласования оформляется договор через юристов, затем создаётся процесс закупки.",
    icon: "📜",
  },
  stock_check: {
    label: "Проверка наличия на складе (от оператора)",
    description:
      "Проверяется наличие на складе. Если найдено — закрывается; если нет — создаётся заявка на приобретение.",
    icon: "📦",
  },
  service_request: {
    label: "Заявка на услуги (от оператора)",
    description:
      "Заявка руководителя подразделения на оказание сторонних услуг (ремонт, обслуживание, обучение).",
    icon: "🛠",
  },
};

export function routeDisplay(code: string) {
  return ROUTE_TYPE_DISPLAY[code] || {
    label: code,
    description: "",
    icon: "📄",
  };
}

// ===== Типы документов во вложениях (doc_kind) =====

export const DOC_KIND_LABEL: Record<string, string> = {
  request: "Заявка",
  invoice: "Счёт",
  contract: "Договор",
  price_list: "Прайс",
  act: "Акт / накладная",
  requisites: "Реквизиты",
  other: "Прочее",
};

export const DOC_KIND_ICON: Record<string, string> = {
  request: "📝",
  invoice: "💳",
  contract: "📜",
  price_list: "🏷️",
  act: "📦",
  requisites: "🏛️",
  other: "📎",
};

export const DOC_KIND_ORDER: string[] = [
  "request",
  "invoice",
  "contract",
  "price_list",
  "act",
  "requisites",
  "other",
];

// ===== Типы заявок руководителя =====

export const REQUEST_TYPE_INFO: Record<RequestType, RouteTypeInfo> = {
  purchase_request: ROUTE_TYPE_INFO.purchase_request,
  tech_task_contract: ROUTE_TYPE_INFO.tech_task_contract,
  stock_check: ROUTE_TYPE_INFO.stock_check,
  service_request: ROUTE_TYPE_INFO.service_request,
};

export const REQUEST_TYPE_LABEL: Record<RequestType, string> = {
  purchase_request: "Приобретение",
  tech_task_contract: "Договор поставки",
  stock_check: "Проверка склада",
  service_request: "Услуги/работы",
};

// ===== Approval Step =====

export const APPROVAL_STEP_TYPE_LABEL: Record<ApprovalStepType, string> = {
  role: "По роли",
  user: "Конкретный пользователь",
  department_head: "Руководитель отдела",
  group: "Группа согласования",
  initiator_manager: "Руководитель автора",
};

export const APPROVAL_STEP_STATUS_LABEL: Record<ApprovalStepStatus, string> = {
  pending: "Ожидает",
  approved: "Согласовано",
  rejected: "Отклонено",
  skipped: "Пропущено",
  clarified: "На доработке",
};

export const APPROVAL_STEP_STATUS_COLOR: Record<ApprovalStepStatus, string> = {
  pending: "bg-neutral-soft text-neutral-ink",
  approved: "bg-success-soft text-success-ink",
  rejected: "bg-danger-soft text-danger-ink",
  skipped: "bg-neutral-soft text-muted",
  clarified: "bg-warning-soft text-warning-ink",
};

/** Подпись приоритета закупки. */
export const PRIORITY_LABEL: Record<string, string> = {
  low: "Низкий",
  normal: "Обычный",
  high: "Высокий",
  urgent: "Срочный",
};

/** Отдел по справочнику: название, а не код («IT-отдел», не «it»).
 *  Код — только если отдела в справочнике уже нет. */
export function departmentLabel(p: Pick<Process, "department" | "department_name">): string | null {
  return p.department_name || p.department || null;
}

/** Инициатор заявки: «ФИО (Отдел)» — по справочникам пользователей и отделов.
 *
 *  Генеральный директор в «Согласованиях» видел в столбце «Инициатор» код
 *  подразделения, а не человека. Имя берётся из справочника пользователей
 *  (актуальное), снимок на момент подачи — запасной вариант.
 */
export function initiatorLabel(
  p: Pick<Process, "initiator_name" | "department" | "department_name" | "context">,
): string {
  const ctx = (p.context || {}) as Record<string, unknown>;
  const name =
    p.initiator_name ||
    (ctx.initiator_full_name as string | undefined) ||
    (ctx.initiator_email as string | undefined) ||
    null;
  const dept = departmentLabel(p);
  if (name && dept) return `${name} (${dept})`;
  return name || dept || "—";
}

/** «Согласующий» в карточке закупки.
 *
 *  После решения — кто и когда решил. До решения — назначенный, а если его
 *  нет, то кто вправе решать. Раньше у согласованной закупки на складе
 *  стояло «назначается на стадии согласования»: поле approver_id у
 *  закупок без маршрута по категории пусто весь путь.
 */
export function approverText(
  p: Pick<Process, "approved_by_name" | "approved_at" | "approval_decision" | "approver_name" | "owner_purchaser_name">,
): string {
  if (p.approved_at) {
    const when = new Date(p.approved_at).toLocaleDateString("ru-RU");
    const verb = p.approval_decision === "rejected" ? "отклонил(а)" : "согласовал(а)";
    return `${p.approved_by_name ?? "—"} — ${verb} ${when}`;
  }
  if (p.approver_name) return p.approver_name;
  return p.owner_purchaser_name
    ? `не назначен — решает ${p.owner_purchaser_name} или согласующий`
    : "не назначен — решает любой согласующий или закупщик";
}

/** «Исполнитель» — ведущий закупщик: он ведёт заказ и поставку. */
export function executorText(p: Pick<Process, "owner_purchaser_name" | "assignee_name">): string {
  return p.owner_purchaser_name ?? p.assignee_name ?? "не назначен";
}

/** Что уйдёт поставщику в письме о согласовании — до нажатия, а не после.
 *
 *  Автор: «в письме уходит сумма, боюсь, чтобы там не было ошибок».
 *  Согласующий видит сумму и номер счёта в окне подтверждения и может
 *  вернуться и поправить сумму («Счёт от поставщика» → «изменить сумму»).
 */
export function approvalLetterLine(amount?: number | null, number?: string | null): string {
  if (amount == null) {
    return "Сумма счёта не указана — в письме поставщику её не будет. Проверьте счёт перед согласованием.";
  }
  const money = amount.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `В письме поставщику: счёт${number ? ` № ${number}` : ""} на сумму ${money} ₽. Если сумма неверна — сначала исправьте её в карточке закупки.`;
}

/** То же для полной карточки закупки: данные счёта лежат в контексте. */
export function approvalLetterLineFor(p: Pick<Process, "context">): string {
  const inv = ((p.context || {}) as Record<string, unknown>).invoice as
    | { amount?: number | null; number?: string | null; invoice_number?: string | null }
    | undefined;
  return approvalLetterLine(inv?.amount ?? null, inv?.number ?? inv?.invoice_number ?? null);
}

